var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React from "react";
import { ThemeProvider as JSSProvider, createUseStyles } from "react-jss";
import merge from "lodash/merge";
var colors = {
    black: "#000",
    gray: "#888",
    grayDark: "#151515",
    grayMid: "#747676",
    grayLight: "#d5d5d5",
    grayLighter: "#f4f4f4",
    white: "#fff",
    asurion: {
        purple: "#8223D2",
        blue: "#647DE1",
        green: "#6EFAC3",
        yellow: "#D2FA46",
        darkGray: "#333F48",
        mediumGray: "#A5AAAF",
        lightGray: "#E6E6EB",
    },
};
var typography = {
    bodyColor: colors.black,
    fontFamily: '"Apercu Pro", "neue-haas-grotesk-display", sans-serif',
    fontSize: "100%",
    title: {
        fontSize: 75,
        fontSizeMobile: 48,
        fontSizeTablet: 75,
        lineHeight: "1",
        fontWeight: "bold",
    },
    heading2: {
        fontSize: 60,
        fontSizeMobile: 40,
        fontSizeTablet: 48,
        lineHeight: "1",
        fontWeight: "bold",
    },
    heading3: {
        fontSize: 40,
        fontSizeMobile: 24,
        fontSizeTablet: 24,
        lineHeight: "1",
        fontWeight: "bold",
    },
    heading4: {
        fontSize: 32,
        fontSizeMobile: 24,
        fontSizeTablet: 24,
        lineHeight: "1",
        fontWeight: "bold",
    },
    heading5: {
        fontSize: 32,
        fontSizeMobile: 32,
        fontSizeTablet: 32,
        lineHeight: "1",
        fontWeight: 300,
    },
    heading6: {
        fontSize: 24,
        fontSizeMobile: 24,
        fontSizeTablet: 24,
        lineHeight: "1",
        fontWeight: 300,
    },
    body: {
        fontSize: 16,
        fontSizeMobile: 16,
        fontSizeTablet: 16,
        lineHeight: "1.5",
        fontWeight: "normal",
    },
};
var breakpoints = {
    small: 512,
    medium: 768,
    large: 1100,
};
var mediaQuery = function (minWidth) {
    return "@media (min-width: " + minWidth + "px)";
};
var maxWidth = 1440;
export var defaultTheme = {
    colors: colors,
    typography: typography,
    breakpoints: breakpoints,
    mediaQuery: mediaQuery,
    maxWidth: maxWidth,
};
var useGlobalStyles = createUseStyles(function (theme) { return ({
    "@global": {
        /* Eric Meyer Reset v2.0 */
        /* https://meyerweb.com/eric/tools/css/reset/ */
        "html, body, div, span, applet, object, iframe, h1, h2, h3, h4, h5, h6, p, blockquote, pre, a, abbr, acronym, address, big, cite, code, del, dfn, em, img, ins, kbd, q, s, samp, small, strike, strong, sub, sup, tt, var, b, u, i, center, dl, dt, dd, ol, ul, li, fieldset, form, label, legend, table, caption, tbody, tfoot, thead, tr, th, td, article, aside, canvas, details, embed, figure, figcaption, footer, header, hgroup, menu, nav, output, ruby, section, summary, time, mark, audio, video": {
            margin: "0",
            padding: "0",
            border: "0",
            font: "inherit",
            verticalAlign: "baseline",
        },
        "article, aside, details, figcaption, figure, footer, header, hgroup, menu, nav, section": {
            display: "block",
        },
        strong: {
            fontWeight: "bold",
        },
        "ol, ul": {
            listStyle: "none",
        },
        "blockquote, q": {
            quotes: "none",
        },
        "blockquote:before, blockquote:after, q:before, q:after": {
            content: "''",
        },
        table: {
            borderCollapse: "collapse",
            borderSpacing: "0",
        },
        /* Globals */
        "html, body": {
            width: "100%",
            minHeight: "100%",
            fontFamily: theme.typography.fontFamily || "sans-serif",
            color: theme.typography.bodyColor || "#000",
            backgroundColor: "#ffffff",
            "-webkit-font-smoothing": "antialiased",
            "-moz-osx-font-smoothing": "grayscale",
            margin: "0",
            padding: "0",
        },
        /* Box-sizing border-box */
        /* https://www.paulirish.com/2012/box-sizing-border-box-ftw/ */
        html: {
            boxSizing: "border-box",
            background: "transparent",
            fontSize: "62.5%",
        },
        "*, *:before, *:after": {
            boxSizing: "inherit",
        },
        code: {
            color: theme.colors.asurion.purple,
            fontFamily: "monospace",
            backgroundColor: theme.colors.asurion.lightGray,
            padding: 4,
        },
        a: {
            fontWeight: "bold",
            color: "inherit",
        },
        /* Reset `button` to nothing */
        /* https://gist.github.com/chrisheninger/a860f87ef4e529b2df606768b97665a8 */
        button: {
            display: "inline-block",
            "-webkit-appearance": "none",
            "-moz-appearance": "none",
            userSelect: "none",
            textDecoration: "none",
            fontFamily: "inherit",
            fontSize: "inherit",
            color: "inherit",
            cursor: "pointer",
            border: "none",
            backgroundColor: "transparent",
            borderColor: "transparent",
            padding: "0",
            margin: "0",
            "&:disabled": {
                opacity: 0.6,
                cursor: "not-allowed",
            },
        },
    },
}); });
var GlobalsAndReset = function () {
    useGlobalStyles();
    return null;
};
var ThemeProvider = function (_a) {
    var children = _a.children, theme = _a.theme, _b = _a.withReset, withReset = _b === void 0 ? true : _b, rest = __rest(_a, ["children", "theme", "withReset"]);
    var mergedTheme = theme ? merge(defaultTheme, theme) : defaultTheme;
    return (React.createElement(JSSProvider, __assign({ theme: mergedTheme }, rest),
        withReset && React.createElement(GlobalsAndReset, null),
        children));
};
export default ThemeProvider;
